import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Seo, { baseOrgSchema } from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangaCategories, mangaList, allTags } from '@/mocks/manga';
import { siteConfig } from '@/config/site';

function Category() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const tag = searchParams.get('tag') || '';

  const current = mangaCategories.find((c) => c.slug === slug);

  const filtered = useMemo(() => {
    return mangaList.filter((m) => {
      if (current && m.category !== current.name) return false;
      if (status && m.status !== status) return false;
      if (tag && !m.tags.includes(tag)) return false;
      return true;
    });
  }, [current, status, tag]);

  const setFilter = (key: 'status' | 'tag', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: false });
  };

  const categoryTitle = current ? `${current.name}漫画` : status === 'finished' ? '完结漫画' : status === 'serializing' ? '连载中漫画' : tag ? `${tag}标签漫画` : '全部漫画';

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '题材分类', path: '/category' },
    ...(current ? [{ name: current.name }] : []),
    ...(status === 'finished' ? [{ name: '完结漫画' }] : status === 'serializing' ? [{ name: '连载中漫画' }] : []),
    ...(tag ? [{ name: `${tag}标签` }] : []),
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${siteConfig.url}/` },
      { '@type': 'ListItem', position: 2, name: '题材分类', item: `${siteConfig.url}/category` },
      ...(current
        ? [{ '@type': 'ListItem', position: 3, name: current.name, item: `${siteConfig.url}/category/${current.slug}` }]
        : []),
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - ${siteConfig.name}`,
    url: `${siteConfig.url}/category${slug ? `/${slug}` : ''}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filtered.slice(0, 12).map((m, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: m.title,
        url: `${siteConfig.url}/article/${m.slug}`,
      })),
    },
  };

  return (
    <>
      <Seo
        title={`${categoryTitle} - 在线看`}
        path={`/category${slug ? `/${slug}` : ''}`}
        description={`浏览${categoryTitle}，在${siteConfig.name}在线阅读${categoryTitle}，支持按类型、标签、状态筛选，人气推荐直接看。`}
        jsonLd={[baseOrgSchema, webpageSchema, breadcrumbSchema]}
      />

      {/* 顶部横幅 */}
      <section className="relative mt-16 flex min-h-[220px] w-full items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=abstract%20warm%20anime%20ink%20brush%20background%20with%20vermilion%20orange%20and%20cream%20swirls%2C%20artistic%20manga%20texture%2C%20soft%20gradient%2C%20subtle%20depth%2C%20editorial%20page%20banner&width=1600&height=500&seq=cat-banner-01&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-foreground-950/20"></div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">{categoryTitle}</h1>
          <p className="mt-2 max-w-2xl text-sm text-background-100 md:text-base">
            {current?.description || (status === 'finished' ? '已经完结的漫画，一口气看完更畅快' : status === 'serializing' ? '持续连载中的漫画，追更不迷路' : tag ? `包含「${tag}」标签的漫画作品` : '按类型、标签、状态筛选，找到你喜欢的漫画')}
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
        {/* 分类切换 */}
        <nav className="mb-6 flex flex-wrap items-center gap-2" aria-label="分类导航">
          <Link
            to="/category"
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${!current && !status && !tag ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
          >
            全部
          </Link>
          {mangaCategories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${current?.slug === c.slug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
            >
              {c.name}
            </Link>
          ))}
        </nav>

        {/* 状态筛选 */}
        <div className="mb-6 flex flex-wrap items-center gap-2" role="group" aria-label="状态筛选">
          <button
            type="button"
            onClick={() => setFilter('status', '')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${!status ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
          >
            全部状态
          </button>
          <button
            type="button"
            onClick={() => setFilter('status', 'finished')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${status === 'finished' ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
          >
            完结漫画
          </button>
          <button
            type="button"
            onClick={() => setFilter('status', 'serializing')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${status === 'serializing' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
          >
            连载中
          </button>
        </div>

        {/* 标签筛选 */}
        <div className="mb-8 flex flex-wrap items-center gap-2" aria-label="标签筛选">
          <span className="text-sm font-bold text-foreground-700">
            <i className="ri-price-tag-3-line mr-1 text-primary-500"></i>标签：
          </span>
          <button
            type="button"
            onClick={() => setFilter('tag', '')}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${!tag ? 'bg-accent-500 text-background-50' : 'bg-accent-100 text-accent-800 hover:bg-accent-500 hover:text-background-50'}`}
          >
            不限
          </button>
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setFilter('tag', tag === t ? '' : t)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${tag === t ? 'bg-accent-500 text-background-50' : 'bg-accent-100 text-accent-800 hover:bg-accent-500 hover:text-background-50'}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">共找到 <strong className="text-primary-600">{filtered.length}</strong> 部漫画</p>
          {(status || tag || current) && (
            <button type="button" onClick={() => setSearchParams({})} className="text-sm font-bold text-primary-600 hover:text-primary-700">
              <i className="ri-refresh-line mr-1"></i>重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
            {filtered.map((item) => (
              <MangaCard key={item.slug} item={item} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-20 text-center">
            <i className="ri-inbox-archive-line text-5xl text-foreground-400"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到符合条件的漫画，试试切换筛选条件吧</p>
          </div>
        )}
      </div>
    </>
  );
}

export default Category;